import { Assets, Texture } from 'pixi.js';

type CosmeticAssetKind = 'flag' | 'pattern' | 'accessory';

const COSMETIC_TEXTURE_PATHS: Record<CosmeticAssetKind, Record<string, string[]>> = {
  flag: {
    cosm_flag_spain: ['/assets/cosmetics/flags/spain.png', '/assets/flags/cosmetic-es.svg'],
    cosm_flag_portugal: ['/assets/cosmetics/flags/portugal.png', '/assets/flags/cosmetic-pt.svg'],
    cosm_flag_france: ['/assets/cosmetics/flags/france.png', '/assets/flags/cosmetic-fr.svg'],
    cosm_flag_germany: ['/assets/cosmetics/flags/germany.png', '/assets/flags/cosmetic-de.svg'],
    cosm_flag_italy: ['/assets/cosmetics/flags/italy.png', '/assets/flags/cosmetic-it.svg'],
    cosm_flag_uk: ['/assets/cosmetics/flags/uk.png', '/assets/flags/cosmetic-gb.svg'],
    cosm_flag_usa: ['/assets/cosmetics/flags/usa.png', '/assets/flags/cosmetic-us.svg'],
  },
  pattern: {
    cosm_pattern_stripes: ['/assets/cosmetics/patterns/stripes.png'],
    cosm_pattern_rings: ['/assets/cosmetics/patterns/rings.png'],
    cosm_pattern_dots: ['/assets/cosmetics/patterns/dots.png'],
    cosm_pattern_chevron: ['/assets/cosmetics/patterns/waves.png'],
    cosm_pattern_zebra: ['/assets/cosmetics/patterns/zebra.png'],
    cosm_pattern_camo: ['/assets/cosmetics/patterns/camo.png'],
    cosm_pattern_lightning: ['/assets/cosmetics/patterns/lightning.png'],
    cosm_pattern_scale: ['/assets/cosmetics/patterns/scales.png'],
    cosm_pattern_mosaic: ['/assets/cosmetics/patterns/mosaic.png'],
    cosm_pattern_galaxy: ['/assets/cosmetics/patterns/galaxy.png'],
  },
  accessory: {
    cosm_acc_round_glasses: ['/assets/cosmetics/accessories/round-glasses.png'],
    cosm_acc_sunglasses: ['/assets/cosmetics/accessories/sunglasses.png'],
    // Legacy alias: old loadouts may still have headband equipped.
    cosm_acc_headband: ['/assets/cosmetics/accessories/headband.png'],
    cosm_acc_monocle: ['/assets/cosmetics/accessories/monocle.png'],
    cosm_acc_mask: ['/assets/cosmetics/accessories/mask.png'],
    cosm_acc_crown: ['/assets/cosmetics/accessories/crown.png'],
    cosm_acc_tophat: ['/assets/cosmetics/accessories/tophat.png'],
    cosm_acc_scarf: ['/assets/cosmetics/accessories/scarf.png'],
    cosm_acc_halo: ['/assets/cosmetics/accessories/halo.png'],
    cosm_acc_wings: ['/assets/cosmetics/accessories/wings.png'],
    cosm_acc_eyepatch: ['/assets/cosmetics/accessories/headband.png'],
  },
};

class AssetManagerImpl {
  private initialized = false;
  private initialization: Promise<void> | null = null;
  private loadedTextures = new Map<string, Texture>();
  private loadedPaths = new Map<string, string>();
  private warnedMissing = new Set<string>();
  loadedCount = 0;
  textureMemoryBytes = 0;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initialization) return this.initialization;
    this.initialization = this.preloadTextures().finally(() => {
      this.initialized = true;
      this.initialization = null;
    });
    return this.initialization;
  }

  private async preloadTextures(): Promise<void> {
    const entries = Object.entries(COSMETIC_TEXTURE_PATHS).flatMap(([kind, records]) =>
      Object.entries(records).map(([id, paths]) => ({ kind: kind as CosmeticAssetKind, id, paths }))
    );

    await Promise.all(
      entries.map(async ({ kind, id, paths }) => {
        const loaded = await this.loadFirstAvailable(paths);
        if (!loaded) {
          console.warn(`[AssetManager] Missing ${kind} texture for ${id}; using fallback renderer`);
          return;
        }

        this.loadedTextures.set(id, loaded.texture);
        this.loadedPaths.set(id, loaded.path);
        this.loadedCount++;
        this.textureMemoryBytes += Math.max(1, loaded.texture.width) * Math.max(1, loaded.texture.height) * 4;
      })
    );
  }

  private async loadFirstAvailable(paths: string[]): Promise<{ texture: Texture; path: string } | null> {
    for (const path of paths) {
      try {
        const texture = await Assets.load<Texture>(path);
        return { texture, path };
      } catch {
        continue;
      }
    }
    return null;
  }

  getFlagTexture(flagId: string): Texture | null {
    return this.getTexture('flag', flagId);
  }

  getPatternTexture(patternId: string): Texture | null {
    return this.getTexture('pattern', patternId);
  }

  getAccessoryTexture(accessoryId: string): Texture | null {
    return this.getTexture('accessory', accessoryId);
  }

  private getTexture(kind: CosmeticAssetKind, id: string): Texture | null {
    const texture = this.loadedTextures.get(id) ?? null;
    if (!texture && !this.warnedMissing.has(id)) {
      this.warnedMissing.add(id);
      console.warn(`[AssetManager] Missing ${kind} texture for ${id}; using fallback renderer`);
    }
    return texture;
  }

  release(): void {
    for (const [id, path] of this.loadedPaths.entries()) {
      void Assets.unload(path).catch(() => undefined);
      this.loadedPaths.delete(id);
    }
    this.loadedTextures.clear();
    this.loadedCount = 0;
    this.textureMemoryBytes = 0;
    this.initialized = false;
    this.initialization = null;
    this.warnedMissing.clear();
  }
}

export const AssetManager = new AssetManagerImpl();
